import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useSelector } from 'hooks';
import { computeDiscountedPrice, computeTax } from 'utils/priceUtils';
import useLabelTranslation from 'hooks/useLabelTranslation';
import { getPayerById } from 'modules/selfServices/selectors';
import type { RepairOrder } from 'modules/selfServices/types/SelfService';
import { ChecklistSeverityEnum } from 'types/Checklist';

import type { Extra } from 'types/ExtrasForm';

import Icon from 'components/ui/Icon';
import InclTaxLabel from 'components/ui/InclTaxLabel';
import Button, { ButtonType } from '../../ui/Button';
import { usePriceFormatter } from 'utils/PriceFormatter/usePriceFormatter';

type BasketItemsProps = (RepairOrder | Extra) &
Pick<RepairOrder, 'payerId'> & {
  vat: number;
  adviceId?: string;
  onRemove?: () => void;
  severity?: ChecklistSeverityEnum;
};

const Item: React.FC<BasketItemsProps> = ({
  vat,
  price,
  payerId,
  adviceId,
  onRemove,
  adviceName,
  coefficient,
  discountedPrice,
  severity,
}) => {
  const intl = useIntl();
  const { getLabelTranslation } = useLabelTranslation();
  const priceFormatter = usePriceFormatter();

  const payer = useSelector((state) => getPayerById(state, payerId));

  const canRemove = typeof onRemove === 'function';
  const isOffered = severity === ChecklistSeverityEnum.OFFERED;
  const showDiscount = price !== discountedPrice || isOffered;

  const showPrice = discountedPrice >= 0;
  const name = getLabelTranslation(adviceName);
  const payerName = payer ? getLabelTranslation(payer.name) : undefined;
  const priceWithVAT = React.useMemo(() => computeTax(price, vat, coefficient), [coefficient, price, vat]);
  const discountedPriceWithVAT = React.useMemo(
    () => computeDiscountedPrice({ price, discountedPrice, coefficient }, vat),
    [coefficient, discountedPrice, price, vat],
  );

  return (
    <>
      <div className="flex justify-between items-center px-2xs">
        <span className="text-xs font-bold">{name}</span>
        <div className="flex items-center">
          {showPrice && (
            <div className="flex flex-col items-end justify-center">
              {isOffered && <span className="font-bold text-xs"><FormattedMessage id="basket.offered" defaultMessage="Offered" /></span>}
              {!isOffered && (
                <span className="font-bold text-xs">
                  {priceFormatter(discountedPriceWithVAT)}
                </span>
              )}
              {!isOffered && <InclTaxLabel />}
              {showDiscount && (
                <span className="italic text-xs line-through">
                  {priceFormatter(priceWithVAT)}
                </span>
              )}
            </div>
          )}
          {canRemove && (
            <Button
              onClick={onRemove}
              type={ButtonType.SECONDARY}
              testId={`basket-remove-${adviceId}`}
              className="ml-xs size-2xl p-3xs! rounded-button flex justify-center items-center"
              aria-label={intl.formatMessage({ id: 'accessibility.button.deleteItems', defaultMessage: 'Delete the item' })}
            >
              <Icon className="fb-icon--delete text-2xl kiosk:text-4xl"/>
            </Button>
          )}
        </div>
      </div>
      {payer && (
        <div className="text-right text-lowest text-xs italic truncate font-semibold px-2xs">
          <FormattedMessage id="billing.payerLabel" defaultMessage="Billing: {payerName}" values={{ payerName }} />
        </div>
      )}
    </>
  );
};

export default Item;
